'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { Stack, Alert, Typography } from '@mui/material';
import { NewsletterStack } from '@/components/SignUp/NewsletterSelector';
import LoadingButton from '@mui/lab/LoadingButton';
import { FormProvider, useForm } from 'react-hook-form';
import { useUser } from '@/utils/supabase/useUser';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import NewSubscriberDetails, {
  subscriberInfoSchema
} from './NewSubscriberDetails';
import { updateNewsletterSubscriptions } from './actions';
import { PAGE_PATHS } from '@/constants/constants';
import { Box } from '@mui/system';

const newslettersSchema = z.object({
  newsletters: z.record(z.boolean())
});

const newSubscriberSchema = z.object({
  ...newslettersSchema.shape,
  ...subscriberInfoSchema.shape
});

const schema = z.union([newslettersSchema, newSubscriberSchema]);

type NewSubscriber = z.infer<typeof newSubscriberSchema>;
type FormData = z.infer<typeof schema>;

export default function NewslettersForm({
  newsletters,
  newsletterStackVariant = 'big'
}: {
  newsletters: Newsletter[];
  newsletterStackVariant?: 'big' | 'small';
}) {
  const [showThankYou, setShowThankYou] = useState(false);
  const { contact, supabase } = useUser();
  const defaultButtonText = contact ? 'SAVE PREFERENCES' : 'SUBSCRIBE';

  const [buttonText, setButtonText] = useState(defaultButtonText);

  const userNewsletters = useMemo(() => {
    return (
      (contact?.mailchimp_interests as Record<string, boolean>) ||
      (contact?.newsletters as Record<string, boolean>) ||
      {}
    );
  }, [contact]);

  const initialNewsletters = useMemo(() => {
    return newsletters.reduce((acc, newsletter) => {
      if (!newsletter.mailchimp_interest) return acc;
      return {
        ...acc,
        [newsletter.mailchimp_interest]:
          userNewsletters[newsletter.mailchimp_interest] || false
      };
    }, {});
  }, [newsletters, userNewsletters]);

  const schema = z.object({
    ...newslettersSchema.shape,
    ...(!contact ? subscriberInfoSchema.shape : {})
  });

  const methods = useForm<FormData>({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    // resolver: async (data, context, options) => {
    //   const result = await zodResolver(schema)(data, context, options);
    //   console.log('Validation Result:', result);
    //   return result;
    // },
    defaultValues: {
      newsletters: initialNewsletters
    }
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    setError,
    reset,
    formState: {
      isSubmitting,
      isValid,
      isDirty,
      isSubmitSuccessful,
      errors,
      isSubmitted
    }
  } = methods;

  const handleNewSubscriber = async (data: NewSubscriber) => {
    const redirectTo = new URL(PAGE_PATHS.authVerify, window.location.origin);

    await supabase.auth.signInWithOtp({
      email: data.email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: redirectTo.toString(),
        data: {
          full_name: `${data.firstName} ${data.lastName}`,
          first_name: data.firstName,
          last_name: data.lastName,
          newsletters: data.newsletters
        }
      }
    });
  };

  const temporaryButtonUpdate = (tempText = 'SAVED', duration = 3000) => {
    setButtonText(tempText);
    setTimeout(() => {
      setButtonText(defaultButtonText);
    }, duration);
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ newsletters: getValues('newsletters') });
    }

    if (isSubmitted && !isSubmitSuccessful) {
      reset(undefined, { keepErrors: true });
    }
  }, [
    isSubmitted,
    isSubmitSuccessful,
    defaultButtonText,
    reset,
    getValues,
    setButtonText
  ]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (contact) {
        await updateNewsletterSubscriptions(data.newsletters);
        temporaryButtonUpdate('SAVED');
      } else if (!contact) {
        const parsedData = newSubscriberSchema.parse(data);
        await handleNewSubscriber(parsedData);
        setShowThankYou(true);
      }
    } catch (error) {
      setError('root', {
        type: 'error',
        message: 'Could not update newsletter subscriptions'
      });
    }
  });

  if (showThankYou) {
    return (
      <Box textAlign='center'>
        <Typography variant='h3' pb={1}>
          Thanks for subscribing!
        </Typography>
        <Typography variant='body1'>
          Just click the link in the confirmation email we sent you to confirm
          your subscription.
        </Typography>
        <Typography variant='body1'>
          You can log in anytime to update your preferences using the email you
          gave us.
        </Typography>
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <Stack useFlexGap alignItems='center'>
          <NewsletterStack
            onNewsletterChange={(value) =>
              setValue('newsletters', value, {
                shouldValidate: true,
                shouldDirty: true
              })
            }
            selectedNewsletters={getValues('newsletters') || {}}
            newsletters={newsletters}
            variant={newsletterStackVariant}
          />
          {!contact && <NewSubscriberDetails />}
          <LoadingButton
            type='submit'
            variant='contained'
            sx={{ width: 200 }}
            disabled={!isValid || !isDirty}
            loading={isSubmitting}
          >
            {buttonText}
          </LoadingButton>
          {errors.root && (
            <Alert severity='success' icon={false}>
              {errors.root.message}
            </Alert>
          )}
        </Stack>
      </form>
    </FormProvider>
  );
}
